import { EventObject } from '@xstate/store-react';
import { StoreContext } from '../../../models/store/context';
import { StoreEvent } from '../../../enums/storeEvent';
import { store } from '../../store';
import { queryModelNumber } from '../../../services/api/queryModelNumber';
import { saveSession } from '../../../services/session/save';
import { AppStatus } from '../../../enums/appStatus';
import { FormKey } from '../../../enums/formKey';
import { StepKey } from '../../../enums/stepKey';
import { log } from '@domgen/shared/src/utils/log';

/**
 * @description Process URL Parameters
 * This event is responsible for processing URL parameters when the application loads.
 * It checks for the presence of specific URL parameters (model number 'mn' and serial number 'sn') in the store context.
 * If a model number is present, it triggers an API call to fetch appliance details based on the model number and updates the store with the results.
 * If only a serial number is present, it updates the form data in the store with the serial number.
 * Finally, it saves the updated session context after processing the URL parameters.
 * @param {StoreContext} context - The current store context
 * @param {EventObject} event - The event object (not used in this case)
 * @param {{ effect: (effect: () => Promise<void>) => void; emit: { navigate: (payload: { path: string; replace?: boolean }) => void } }} enqueue - An object with an effect function to enqueue side effects and an emit function to navigate
 * @returns {StoreContext} Updated context after processing URL parameters and triggering necessary side effects
 */
export const processUrlParams = <T extends EventObject | undefined = EventObject>(
  context: StoreContext,
  _event: T,
  enqueue: {
    effect: (effect: () => Promise<void>) => void;
  },
): StoreContext => {
  log.debug({ message: StoreEvent.PROCESS_URL_PARAMS });

  const mn = context.urlParams?.mn ?? undefined;
  const sn = context.urlParams?.sn ?? undefined;

  if (!mn) {
    const form = { ...context.form };

    if (sn) {
      form[StepKey.MODEL_SERIAL] = {
        ...form[StepKey.MODEL_SERIAL],
        [FormKey.SERIAL_NUMBER]: sn,
      };
    }

    const updatedContext = {
      ...context,
      form,
      app: { ...context.app, status: AppStatus.READY },
    };

    saveSession(updatedContext);
    return updatedContext;
  }

  enqueue.effect(async () => {
    const response = await queryModelNumber(mn);
    const matchedAppliance =
      response.appliances.find((a) => mn === a.modelNumber) ??
      response.appliances.find((a) => mn.includes(a.modelNumber));

    store.send({
      type: StoreEvent.SET_URL_PARAM_RESULTS,
      data: {
        appliances: response.appliances,
        matchedAppliance,
      },
    });
  });

  return context;
};
